import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent as CardBody, CardDescription } from '@shadcn/components/ui/card';
import { Col } from '@shadcn/components/ui/col';
import { Row } from '@shadcn/components/ui/row';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { backendUrl } from '@helpers/consts';


import superagent from 'superagent';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);




const OrderCycleTimeDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await superagent
        .post(`${backendUrl}/orders/orderCycleTimeAnalytics`)
        .send({ startDate, endDate })
        .set('Authorization', `Bearer ${localStorage.getItem("authJWT")}`)
        .set('Accept', 'application/json');
      
      setData(response.body);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch order cycle time data');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const renderHeader = () => (
    <div className="mb-6">
      <Row className="flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <Col className="w-full lg:w-1/2">
          <h2 className="text-2xl font-bold text-gray-800">
            Order Cycle Time Analytics
          </h2>
          <CardDescription className="mt-2">
            Monitor and analyze order cycle times by country and location
          </CardDescription>
        </Col>
        <Col className="w-full lg:w-1/2">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="w-full sm:w-auto flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                className="form-control w-full"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-auto flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                className="form-control w-full"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-auto flex items-end">
              <button 
                className="btn btn-primary w-full sm:w-auto mt-5"
                onClick={fetchData}
              >
                Apply Filter
              </button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );

  const barChartData = useMemo(() => {
    if (!data?.byCountry) return {
      labels: [],
      datasets: []
    };

    const countries = data.byCountry.map(item => item.country);
    
    return {
      labels: countries,
      datasets: [
        {
          label: 'Standard Orders',
          data: data.byCountry.map(item => item.standard.averageTime.days + item.standard.averageTime.hours / 24),
          backgroundColor: '#34c38f',
          borderColor: '#34c38f',
          borderWidth: 1,
          hoverBackgroundColor: '#26a69a',
          hoverBorderColor: '#26a69a',
        },
        {
          label: 'Personalisation Orders',
          data: data.byCountry.map(item => item.personalisation.averageTime.days + item.personalisation.averageTime.hours / 24),
          backgroundColor: '#f1b44c',
          borderColor: '#f1b44c',
          borderWidth: 1,
          hoverBackgroundColor: '#e7a542',
          hoverBorderColor: '#e7a542',
        }
      ]
    };
  }, [data]);

  if(loading) {
    return (
        <div className="px-2 sm:px-4">
        
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-2">Loading order cycle time data...</p>
            </div>
          </div>
        </div>

    );
  }

  if (error) {
    return (

        <div className="px-2 sm:px-4">
          {renderHeader()}
          <div className="text-center p-5">
            <h4>Error loading data</h4>
            <p className="text-danger">{error}</p>
            <button 
              className="btn btn-primary mt-3"
              onClick={() => window.location.reload()}
            >
              Try Again
            </button>
          </div>
        </div>
      
    );
  }

  if (!data) {
    return (
      
        <div className="px-2 sm:px-4">
          
           {renderHeader()}
          <div className="text-center p-5">
            <h4>No data available</h4>
            <p>Unable to load order cycle time data. Please try again later.</p>
          </div>
        </div>
      
    );
  }

  return (
    <React.Fragment>
      
        <div className="px-2 sm:px-4">
       {renderHeader()}
          
          <Row className="mb-4">
            <Col className="w-full">
              <Card>
                <CardBody className="py-4 px-5">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Card className="mini-stats-wid bg-light h-full">
                        <CardBody className="h-full p-4">
                          <div className="flex h-full">
                            <div className="flex-grow-1">
                              <p className="text-gray-700 fw-semibold text-sm mb-1">Total Orders</p>
                              <h4 className="mb-0 text-lg font-semibold">{data.summary.totalOrders}</h4>
                            </div>
                            <div className="avatar-xs rounded-circle bg-primary align-self-center mini-stat-icon">
                              <span className="avatar-title rounded-circle bg-primary">
                                <i className="bx bx-shopping-bag font-size-20"></i>
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                    <div>
                      <Card className="mini-stats-wid bg-light h-full">
                        <CardBody className="h-full p-4">
                          <div className="flex h-full">
                            <div className="flex-grow-1">
                              <p className="text-gray-700 fw-semibold text-sm mb-1">Standard Orders</p>
                              <h4 className="mb-0 text-lg font-semibold">{data.summary.standardOrders}</h4>
                              <p className="text-success text-sm mt-1 mb-0">
                                

                                {( 
                                  data.summary.standardOrders / data.summary.totalOrders?
                                  (data.summary.standardOrders / data.summary.totalOrders) * 100:
                                  0
                                ).toFixed(1)}%
                              </p>
                            </div>
                            <div className="avatar-xs rounded-circle bg-success align-self-center mini-stat-icon">
                              <span className="avatar-title rounded-circle bg-success">
                                <i className="bx bx-check-circle font-size-20"></i>
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                    <div>
                      <Card className="mini-stats-wid bg-light h-full">
                        <CardBody className="h-full p-4">
                          <div className="flex h-full">
                            <div className="flex-grow-1">
                              <p className="text-gray-700 fw-semibold text-sm mb-1">Personalisation Orders</p>
                              <h4 className="mb-0 text-lg font-semibold">{data.summary.personalisationOrders}</h4>
                              <p className="text-warning text-sm mt-1 mb-0">
                                {(
                                  data.summary.personalisationOrders / data.summary.totalOrders ?
                                  (data.summary.personalisationOrders / data.summary.totalOrders) * 100:
                                  0
                                ).toFixed(1)}%
                              </p>
                            </div>
                            <div className="avatar-xs rounded-circle bg-warning align-self-center mini-stat-icon">
                              <span className="avatar-title rounded-circle bg-warning">
                                <i className="bx bx-paint font-size-20"></i>
                              </span>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <div className="grid grid-cols-1 gap-4 mb-4">
            <div className="lg:col-span-12">
              <Card>
                <CardBody>
                  <h4 className="card-title my-4">Average Order Cycle Time by Country</h4>
                  <div className="h-[400px]">
                    <Bar 
                      data={barChartData} 
                      options={{
                        maintainAspectRatio: false,
                        responsive: true,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Average Time (Days)'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Country'
                            }
                          }
                        }
                      }} 
                    />
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

          <div className="grid grid-cols-1 mb-4">
            <div>
              <Card>
                <CardBody>
                  <h4 className="card-title my-4">Detailed Country Analysis</h4>
                  <div className="overflow-x-auto">
                    <table className="table table-centered table-nowrap mb-0 min-w-full">
                      <thead className="table-light">
                        <tr>
                          <th className="py-3">Country</th>
                          <th className="py-3">Standard Orders</th>
                          <th className="py-3">Standard Time</th>
                          <th className="py-3">Personalisation Orders</th>
                          <th className="py-3">Personalisation Time</th>
                          <th className="py-3">Time Difference</th>
                          <th className="py-3">% Difference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.byCountry.map(country => {
                          const canCompare = country.standard.count > 0 && country.personalisation.count > 0;

                          return (
                            <tr key={country.country}>
                              <td className="py-3 font-medium">{country.country}</td>
                              <td className="py-3">
                                <span className="text-success">{country.standard.count}</span>
                              </td>
                              <td className="py-3">
                                <span className="text-success">{country.standard.displayTime}</span>
                              </td>
                              <td className="py-3">
                                <span className="text-warning">{country.personalisation.count}</span>
                              </td>
                              <td className="py-3">
                                <span className={canCompare ? 'text-warning' : 'text-dark'}>
                                  {canCompare ? country.personalisation.displayTime : '-'}
                                </span>
                              </td>
                              <td className="py-3">
                                <span className={canCompare ? (country.comparison.percentageDifference >= 0 ? 'text-success' : 'text-danger') : 'text-dark'}>
                                  {canCompare
                                    ? (country.comparison.displayDifference.startsWith('-') ? country.comparison.displayDifference.substring(1) : country.comparison.displayDifference)
                                    : '-'}
                                </span>
                              </td>
                              <td className="py-3">
                                <span className={canCompare ? (country.comparison.percentageDifference >= 0 ? 'text-success' : 'text-danger') : 'text-dark'}>
                                  {canCompare ? `${Math.abs(country.comparison.percentageDifference)}%` : '-'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

        
        </div>
      
    </React.Fragment>
  );
};

export default OrderCycleTimeDashboard;
